import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { appSupportService } from "@/features/app-support/services/appSupportService";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Button } from "@/shared/components/ui/Button";
import { Badge } from "@/shared/components/ui/Badge";
import { EmptyState } from "@/shared/components/feedback/EmptyState";
import { formatDateTime } from "@/shared/utils/formatters";
import { AppSupportDetailPanel } from "@/features/app-support/components/AppSupportDetailPanel";
import type {
  AppSupportTicket,
  AppSupportStatus,
  AppSupportSeverity,
} from "@/features/app-support/types/appSupport";
import {
  STATUS_LABELS,
  SEVERITY_LABELS,
  CATEGORY_LABELS,
  STATUS_OPTIONS,
  SEVERITY_OPTIONS,
  CATEGORY_OPTIONS,
} from "@/features/app-support/types/appSupport";

const statusVariant: Record<AppSupportStatus, "default" | "success" | "warning" | "danger" | "info"> = {
  NEW:             "info",
  TRIAGED:         "default",
  IN_PROGRESS:     "warning",
  WAITING_ON_USER: "warning",
  RESOLVED:        "success",
  CLOSED:          "success",
};

const severityVariant: Record<AppSupportSeverity, "default" | "success" | "warning" | "danger" | "info"> = {
  LOW:    "default",
  NORMAL: "info",
  HIGH:   "warning",
  URGENT: "danger",
};

export const AppSupportPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useAuth();
  const isPlatformAdmin = userData?.is_platform_admin ?? false;
  const isSupportUser   = isPlatformAdmin;

  const [tickets, setTickets]           = useState<AppSupportTicket[]>([]);
  const [loading, setLoading]           = useState(true);
  const [selectedTicket, setSelected]   = useState<AppSupportTicket | null>(null);

  // Filters (support/admin only)
  const [filterStatus,   setFilterStatus]   = useState("All");
  const [filterSeverity, setFilterSeverity] = useState("All");
  const [filterCategory, setFilterCategory] = useState("All");

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const filters: Record<string, string> = {};
      if (isSupportUser) {
        if (filterStatus   !== "All") filters.status   = filterStatus;
        if (filterSeverity !== "All") filters.severity = filterSeverity;
        if (filterCategory !== "All") filters.category = filterCategory;
      }
      const data = await appSupportService.searchTickets(filters);
      setTickets(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [isSupportUser, filterStatus, filterSeverity, filterCategory]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets, isSupportUser]);

  // Deep link from notification
  useEffect(() => {
    const state = location.state as { openTicketId?: number } | null;
    if (state?.openTicketId && tickets.length > 0) {
      const target = tickets.find((t) => t.ticket_id === state.openTicketId);
      if (target) setSelected(target);
    }
  }, [location.state, tickets]);

  const handleUpdated = (updated: AppSupportTicket) => {
    setTickets((prev) => prev.map((t) => t.ticket_id === updated.ticket_id ? updated : t));
    setSelected(updated);
  };

  const pageTitle = isPlatformAdmin
    ? t("app_support.platform_queue")
    : isSupportUser
      ? t("app_support.queue")
      : t("app_support.my_requests");

  return (
    <div>
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">{pageTitle}</h1>
        <Button onClick={() => navigate("/support/new")}>
          {t("app_support.report_problem")}
        </Button>
      </div>

      {/* Filters (support/admin only) */}
      {isSupportUser && (
        <div className="flex flex-wrap gap-3 mb-5">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="All">{t("inquiry.status.all")}</option>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{t(o.label)}</option>
            ))}
          </select>
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="All">{t("app_support.all_severities")}</option>
            {SEVERITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{t(o.label)}</option>
            ))}
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="All">{t("app_support.all_categories")}</option>
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{t(o.label)}</option>
            ))}
          </select>
          <Button variant="secondary" onClick={loadTickets}>
            {t("common.search_e")}
          </Button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : tickets.length === 0 ? (
        <EmptyState
          title={t("app_support.no_tickets")}
          description={t("app_support.no_tickets_desc")}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-16">
                    {t("app_support.sr_number")}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    {t("app_support.title_label")}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-32">
                    {t("app_support.category")}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">
                    {t("app_support.severity")}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-32">
                    {t("app_support.status")}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">
                    {t("inquiry.tabs.comments")}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">
                    {t("inquiry.tabs.attachments")}
                  </th>
                  {isPlatformAdmin && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-32">
                      {t("app_support.business_unit")}
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-36">
                    {t("inquiry.footer.last_updated")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => (
                  <tr
                    key={ticket.ticket_id}
                    onClick={() => setSelected(ticket)}
                    className="border-b border-gray-50 hover:bg-indigo-50/40 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 font-mono text-gray-400 text-xs">
                      #{ticket.ticket_id}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900 max-w-xs">
                      <span className="line-clamp-2">{ticket.title}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {ticket.category ? t(CATEGORY_LABELS[ticket.category]) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={severityVariant[ticket.severity]}>
                        {t(SEVERITY_LABELS[ticket.severity])}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={statusVariant[ticket.status]}>
                        {t(STATUS_LABELS[ticket.status])}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">
                      {ticket.comment_count ?? 0}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">
                      {ticket.attachments?.length ?? 0}
                    </td>
                    {isPlatformAdmin && (
                      <td className="px-4 py-3 text-gray-500 text-xs font-mono">
                        {ticket.business_unit}
                      </td>
                    )}
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {ticket.updated_at ? formatDateTime(ticket.updated_at) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail panel */}
      <AppSupportDetailPanel
        ticket={selectedTicket}
        isOpen={!!selectedTicket}
        onClose={() => setSelected(null)}
        onUpdated={handleUpdated}
      />
    </div>
  );
};
